import { useEffect, useMemo, useRef, useState } from "react";
import { startCase } from "lodash";
import Pagination from "../../components/Pagination";
import ProfitLossCell from "../../Utils/ProfitLossCell";
import { formatDate, toFixedWithoutRounding } from "../../Utils/Constant";
import { apiPath } from "../../Utils/apiPath";
import { groupEventBets, useMatchFeed } from "./useSportAnalytics";

const marketTypes = [
  ["all", "All Markets"], ["betfair", "Match Odds"], ["bookmaker", "Bookmaker"],
  ["fancy", "Fancy"], ["sportBook", "Sportsbook"],
];
const selectionLabel = (bet) => bet.teamName || bet.selectionName || bet.runnerName ||
  (bet.betRun != null ? `${bet.betRun} / ${bet.bhav ?? "-"}` : "-");
const marketLabel = (bet) => bet.fancyName || bet.market || startCase(bet.betFaireType);
const betKey = (bet, index) => `${bet._id || bet.matchBetId || bet.sessionBetId || index}-${bet.betFaireType}`;
const isBack = (bet) => bet.betType === "back" || bet.type === "Yes";
const dateLabel = (date) => date ? formatDate(date) : "-";

export function RecentBets({ groups, onAllBets, onProfitLoss }) {
  return <aside className="analytics-recent" aria-label="Recent bets">
    <div className="analytics-bets-heading">
      <h3>Recent Bets</h3>
      <div className="analytics-actions">
        <button type="button" className="btn" onClick={onAllBets}>All Bets</button>
        <button type="button" className="btn" onClick={onProfitLoss}>P&amp;L</button>
      </div>
    </div>
    <div className="analytics-counts">
      {marketTypes.slice(1).map(([key, label]) => <span key={key}>{label}: <strong>{groups[key].length}</strong></span>)}
    </div>
    <div className="table-responsive">
      <table className="table01 analytics-bet-table">
        <thead><tr><th className="align-L">UserName</th><th className="align-L">Market</th><th className="align-L">Runner</th><th>Rate</th><th>Amount</th></tr></thead>
        <tbody>{groups.all.slice(0, 25).map((bet, index) => <tr key={betKey(bet, index)} className={isBack(bet) ? "analytics-back-bet" : "analytics-lay-bet"}>
          <td className="align-L">{bet.clientName || "-"}</td><td className="align-L">{marketLabel(bet)}</td>
          <td className="align-L">{selectionLabel(bet)}</td><td>{bet.bhav ?? "-"}</td><td>{toFixedWithoutRounding(bet.amount ?? bet.stake ?? 0)}</td>
        </tr>)}
          {!groups.all.length && <tr><td colSpan={5}>No bets found.</td></tr>}
        </tbody>
      </table>
    </div>
  </aside>;
}

export function MatchBetsDialog({ mode, groups, eventId, detail, onClose }) {
  const settled = mode === "settled";
  const feed = useMatchFeed(apiPath.eventsBets, { eventId, status: "completed" }, 15000, settled);
  const settledGroups = useMemo(() => groupEventBets(feed.data), [feed.data]);
  const [market, setMarket] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const dialog = useRef(null);
  const rows = (settled ? settledGroups : groups)[market] || [];
  const totalPages = Math.max(1, Math.ceil(rows.length / limit));
  const currentPage = Math.min(page, totalPages);
  const visibleRows = rows.slice((currentPage - 1) * limit, currentPage * limit);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const controls = dialog.current?.querySelectorAll("button, select, input, a[href], [tabindex='0']");
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      previousFocus?.focus();
    };
  }, [onClose]);

  return <div className="pop_bg analytics-dialog-bg" onClick={onClose}>
    <div className="pop_boxs pop_refer analytics-dialog" ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="match-bets-title" onClick={(event) => event.stopPropagation()}>
      <div className="analytics-bets-heading">
        <h2 id="match-bets-title">{settled ? "Settled Markets P&L" : "All Bets"}</h2>
        <button type="button" className="btn" onClick={onClose}>Close</button>
      </div>
      <p className="analytics-dialog-event">{detail?.eventName} | {detail?.seriesName} | {eventId}</p>
      <div className="function-wrap analytics-dialog-filters">
        <label htmlFor="match-bet-market">Market Type</label>
        <select id="match-bet-market" value={market} onChange={(event) => { setMarket(event.target.value); setPage(1); }}>
          {marketTypes.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <label htmlFor="match-bet-limit">Rows</label>
        <select id="match-bet-limit" value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}>
          {[25, 50, 100].map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        {settled && <button type="button" className="btn-send" onClick={feed.refresh}>Refresh P&amp;L</button>}
      </div>
      {settled && feed.error && <p role="alert" className="analytics-errors">{feed.error}</p>}
      <div className="table-responsive analytics-dialog-table">
        <table className="table01 tab-depth analytics-bet-table">
          <thead><tr>
            <th className="align-L">PL ID</th><th className="align-L">Bet ID</th>
            <th className="align-L">{settled ? "Declare Date & Time" : "Bet placed"}</th>
            <th className="align-L">IP Address</th><th className="align-L">Market</th>
            <th className="align-L">Selection</th><th>Type</th><th>Odds req.</th><th>Stake</th><th>Liability</th>
            <th>{settled ? "Profit/Loss" : "Pot Profit"}</th>
          </tr></thead>
          <tbody>{visibleRows.map((bet, index) => <tr key={betKey(bet, index)} className={isBack(bet) ? "analytics-back-bet" : "analytics-lay-bet"}>
            <td className="align-L">{bet.clientName || "-"}</td><td className="align-L">{bet.matchBetId || bet.sessionBetId || "-"}</td>
            <td className="align-L">{dateLabel(settled ? bet.declaredAt || bet.updatedAt || bet.timeInserted : bet.timeInserted || bet.createdAt)}</td>
            <td className="align-L">{bet.ipAddress?.replace("::ffff:", "") || "-"}</td>
            <td className="align-L">{marketLabel(bet)}</td><td className="align-L">{selectionLabel(bet)}</td>
            <td><span className={isBack(bet) ? "yes" : "no"}>{startCase(bet.betType || bet.type)}</span></td>
            <td>{bet.betRun != null ? `${bet.betRun} / ${bet.bhav ?? "-"}` : bet.bhav ?? "-"}</td>
            <td>{toFixedWithoutRounding(bet.amount ?? bet.stake ?? 0)}</td><td>{toFixedWithoutRounding(bet.loseAmount ?? 0)}</td>
            {settled ? <ProfitLossCell item={bet} getBetType={bet.betFaireType} filter={{ type: "all" }} /> :
              <td>{toFixedWithoutRounding(bet.profitAmount ?? 0)}</td>}
          </tr>)}
            {!visibleRows.length && <tr><td colSpan={11}>{settled && feed.loading ? "Loading settled bets…" : "No bets found."}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="analytics-entries">Showing {rows.length ? (currentPage - 1) * limit + 1 : 0} – {Math.min(currentPage * limit, rows.length)} of {rows.length} entries</p>
      <Pagination pagination={{ page: currentPage, totalPages, hasPrevPage: currentPage > 1, hasNextPage: currentPage < totalPages }} onPageChange={setPage} />
    </div>
  </div>;
}
